import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays, Users, Building2, FileText, LogOut, X,
  BarChart3, Home, MessageCircle, Key, Database, Activity, Settings, Sparkles
} from "lucide-react";
import { useAuth } from "../contexts";
import { API } from "../config/api";
import { LATEST_RELEASE_DATE } from "../data/releaseNotes";

const Sidebar = ({ isOpen, setIsOpen, wsConnected }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [hasUnseenNews, setHasUnseenNews] = useState(
    () => (localStorage.getItem('lastSeenReleaseDate') || '') < LATEST_RELEASE_DATE
  );

  useEffect(() => {
    const onSeen = () => setHasUnseenNews(false);
    window.addEventListener('release-notes-seen', onSeen);
    return () => window.removeEventListener('release-notes-seen', onSeen);
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('auth.passwordMismatch') || 'Le password non coincidono');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error(t('auth.passwordTooShort') || 'La password deve avere almeno 6 caratteri');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      toast.success(t('auth.passwordChanged') || 'Password cambiata con successo!');
      setShowChangePassword(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.passwordChangeError') || 'Errore nel cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const menuItems = [
    { icon: Home, label: t('nav.dashboard'), path: "/", roles: ["superadmin", "admin", "operator", "quality_control", "it_admin"] },
    { icon: CalendarDays, label: t('nav.calendar'), path: "/calendar", roles: ["superadmin", "admin", "operator", "installer", "quality_control"] },
    { icon: MessageCircle, label: t('nav.chat'), path: "/chat", roles: ["superadmin", "admin", "operator", "installer"] },
    { icon: Building2, label: t('nav.teams'), path: "/teams", roles: ["superadmin", "admin"] },
    { icon: Users, label: t('nav.users'), path: "/users", roles: ["superadmin", "admin"] },
    { icon: FileText, label: t('nav.reports'), path: "/reports", roles: ["superadmin", "admin", "operator"] },
    { icon: BarChart3, label: t('nav.activity'), path: "/activity", roles: ["superadmin", "admin", "operator", "quality_control"] },
    { icon: Database, label: "Backup DB", path: "/backup", roles: ["superadmin"] },
    { icon: Activity, label: "TelcaVoIP AI Monitor", path: "/monitor", roles: ["superadmin"] },
    { icon: Settings, label: "IT Admin", path: "/it-admin", roles: ["superadmin", "it_admin"] },
    { icon: Sparkles, label: "Novità", path: "/news", roles: ["superadmin", "admin", "operator", "installer", "quality_control", "it_admin"] },
  ];

  const filteredMenu = menuItems.filter((item) => item.roles.includes(user?.role));

  return (
    <>
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">{t('auth.changePassword') || 'Cambia Password'}</h3>
              <button onClick={() => setShowChangePassword(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.currentPassword') || 'Password attuale'}
                </label>
                <input
                  type="password"
                  value={passwordData.old_password}
                  onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.newPassword') || 'Nuova password'}
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.confirmPassword') || 'Conferma nuova password'}
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowChangePassword(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  {t('common.cancel') || 'Annulla'}
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="flex-1 px-4 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {changingPassword ? '...' : (t('auth.changePassword') || 'Cambia Password')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-gray-900 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-2xl font-bold text-white">Lo</span>
                <span className="text-2xl font-bold text-lojack-red">/</span>
                <span className="text-2xl font-bold text-white">Jack</span>
              </div>
              {user?.role !== 'it_admin' && (
                <div className="flex items-center gap-1.5" data-testid="ws-status-indicator">
                  <span className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                  <span className={`text-xs ${wsConnected ? 'text-green-400' : 'text-red-400'}`}>
                    {wsConnected ? 'Live' : 'Offline'}
                  </span>
                </div>
              )}
            </div>
            <p className="text-gray-400 text-sm mt-1">{t('auth.installerCalendar')}</p>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
            {filteredMenu.map((item) => {
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${
                    window.location.pathname === item.path
                      ? "bg-lojack-red text-white"
                      : "text-gray-300 hover:bg-gray-800"
                  }`}
                  data-testid={`nav-${item.path.slice(1) || 'dashboard'}`}
                >
                  <item.icon size={20} />
                  <span className="flex-1">{item.label}</span>
                  {item.path === '/news' && hasUnseenNews && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full bg-lojack-red text-white"
                      data-testid="sidebar-news-badge"
                    >
                      NUOVO
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-700 flex-shrink-0">
            <div className="mb-4">
              <p className="text-white font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-gray-400 text-sm">{user?.email}</p>
              <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                user?.role === "superadmin" ? "bg-red-600 text-white font-semibold" :
                user?.role === "admin" ? "bg-purple-600 text-white" :
                user?.role === "operator" ? "bg-blue-600 text-white" :
                "bg-green-600 text-white"
              }`}>
                {t(`roles.${user?.role}`)}
              </span>
            </div>

            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full flex items-center gap-2 px-4 py-2 mb-2 text-gray-300 hover:bg-gray-800 rounded-lg transition"
            >
              <Key size={18} />
              {t('auth.changePassword') || 'Cambia Password'}
            </button>

            {user?.role === "superadmin" && (
              <a
                href="https://telcavoip.ai/it/area-clienti/login"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-2 px-4 py-2 mb-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-medium text-sm"
              >
                <MessageCircle size={18} />
                {t('nav.support') || 'Assistenza TelcaVoIP AI'}
              </a>
            )}

            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-4 py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
